import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import Button from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

const ChoreographerModal = ({ children, onClose }) => {
	const { Modal } = useChoreographerAssets();

	return (
		<Modal onClose={onClose} width="medium">
			{children}
		</Modal>
	);
};

const ATL_COOKIES_URL = 'https://www.atlassian.com/legal/cookies';

const styles = cssMap({
	content: {
		height: '130px',
	},
	contentText: {
		marginBlock: token('space.250'),
		marginInline: 0,
	},
	buttonGroup: {
		marginTop: token('space.250'),
	},
});

export const i18n: {
	googleCookiesNotAllowedModalTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	googleCookiesNotAllowedModalText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	moreOnCookiesButtonLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	gotItButtonLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	googleCookiesNotAllowedModalTitle: {
		id: 'import-from-sources.cookies-not-allowed.title',
		defaultMessage: 'Import unavailable',
		description:
			'Title of the modal dialog shown when user cannot import from Google Docs due to cookie settings.',
	},
	googleCookiesNotAllowedModalText: {
		id: 'import-from-sources.cookies-not-allowed.text',
		defaultMessage:
			'To import from Google Docs, please clear your browser cookies, refresh the page, and allow all cookies.',
		description:
			'Body text of the modal explaining the steps needed to resolve the cookie permission issue blocking Google Docs import.',
	},
	moreOnCookiesButtonLabel: {
		id: 'import-from-sources.cookies-not-allowed.moreOnCookiesAction',
		defaultMessage: 'More on cookies',
		description:
			'Label for the button that opens a link to Atlassian cookies policy documentation. Opens in a new tab.',
	},
	gotItButtonLabel: {
		id: 'import-from-sources.cookies-not-allowed.gotItAction',
		defaultMessage: 'Got it',
		description: 'Label for the button that closes the cookie permission modal.',
	},
});

export const GoogleCookiesModal: React.FC<{
	onClose: () => void;
}> = ({ onClose }) => {
	return (
		<ChoreographerModal onClose={onClose}>
			<ModalHeader hasCloseButton>
				<ModalTitle>
					<FormattedMessage {...i18n.googleCookiesNotAllowedModalTitle} />
				</ModalTitle>
			</ModalHeader>

			<ModalBody>
				<Box xcss={styles.content}>
					<Box as="p" xcss={styles.contentText}>
						<FormattedMessage {...i18n.googleCookiesNotAllowedModalText} />
					</Box>
					<Inline alignInline="end" alignBlock="center" space="space.150" xcss={styles.buttonGroup}>
						<LinkButton
							testId="google-cookies-more-on-cookies-button"
							href={ATL_COOKIES_URL}
							target="_blank"
						>
							<FormattedMessage {...i18n.moreOnCookiesButtonLabel} />
						</LinkButton>
						<Button testId="google-cookies-got-it-button" onClick={onClose}>
							<FormattedMessage {...i18n.gotItButtonLabel} />
						</Button>
					</Inline>
				</Box>
			</ModalBody>
		</ChoreographerModal>
	);
};
